import { type Role, ROLES } from './roles.js';

/**
 * Central capability (permission) model.
 * Drives backend authorization AND frontend navigation/visibility.
 * Adding a capability here automatically flows to both sides.
 */
export const CAPABILITIES = [
  'dashboard.view',
  'transactions.view',
  'transactions.manage',
  'monitoring.view',
  'washing.view',
  'washing.manage',
  'folding.view',
  'folding.manage',
  'purchasing.view',
  'purchasing.manage',
  'inventory.view',
  'inventory.manage',
  'expenses.view',
  'expenses.manage',
  'reports.view',
  'settings.manage',
  'users.read',
  'users.manage',
  'branches.read',
  'pricing.manage',
  'audit.read',
] as const;

export type Capability = (typeof CAPABILITIES)[number];

/** Role -> capabilities. ADMIN implicitly holds every capability. */
export const ROLE_CAPABILITIES: Record<Role, readonly Capability[]> = {
  ADMIN: CAPABILITIES,
  WASHING_EMPLOYEE: ['dashboard.view', 'washing.view', 'washing.manage'],
  FOLDING_EMPLOYEE: ['dashboard.view', 'folding.view', 'folding.manage'],
};

export function capabilitiesForRole(role: Role): Capability[] {
  return [...ROLE_CAPABILITIES[role]];
}

export function hasCapability(role: Role, capability: Capability): boolean {
  return ROLE_CAPABILITIES[role].includes(capability);
}

/**
 * Branch data scope.
 * - ADMIN  -> ALL (cross-branch; branch switcher is only a view filter)
 * - others -> exactly one branch (their assigned branch)
 */
export type BranchScope = { type: 'ALL' } | { type: 'BRANCH'; branchId: string; code: string };

export const ALL_BRANCHES_FILTER = 'ALL';

export function scopeForRole(
  role: Role,
  branchId: string | null | undefined,
  branchCode?: string | null,
): BranchScope {
  if (role === 'ADMIN') return { type: 'ALL' };
  if (!branchId) {
    // Data-integrity error: employee accounts must always have a branch.
    throw new Error('Employee role requires an assigned branch');
  }
  return { type: 'BRANCH', branchId, code: branchCode ?? '' };
}

/** Server-side branch isolation check: can this scope read the given branch's data? */
export function scopeCanAccessBranch(scope: BranchScope, branchId: string | null): boolean {
  if (scope.type === 'ALL') return true;
  return branchId === scope.branchId;
}
